/**
 * Social share intents.
 *
 * Every target gets the *public Grove share URL*, never the raw source URL —
 * the share page is where a recipient meets Grove, so it's the only link we
 * ever hand out. The snippet is the card copy that travels with it.
 */

import { shareSnippet, type ShareSubject as SnippetSubject } from '@grove/api-client';

export interface ShareSubject extends SnippetSubject {
  /** The public Grove share URL. */
  url: string;
}

export interface SocialTarget {
  id: 'x' | 'linkedin' | 'bluesky' | 'reddit';
  label: string;
  href: string;
}

export function socialTargets(subject: ShareSubject): SocialTarget[] {
  const url = encodeURIComponent(subject.url);
  const snippet = shareSnippet(subject);
  const text = encodeURIComponent(snippet);
  // Bluesky has no separate url field — the link goes in the post body.
  const blueskyText = encodeURIComponent(`${snippet}\n\n${subject.url}`);
  const title = encodeURIComponent(subject.title);

  return [
    { id: 'x', label: 'X', href: `https://x.com/intent/post?text=${text}&url=${url}` },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    },
    { id: 'bluesky', label: 'Bluesky', href: `https://bsky.app/intent/compose?text=${blueskyText}` },
    {
      id: 'reddit',
      label: 'Reddit',
      href: `https://www.reddit.com/submit?url=${url}&title=${title}`,
    },
  ];
}

/** Splits a free-typed recipient field ("a@b.com, c@d.com") into addresses. */
export function parseRecipients(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
